// YERİ: src/app/domains/site/sections/personal.sections.ts
//
// KİŞİSEL SİTE BÖLÜMLERİ — portfolyo ızgarası ve özgeçmiş zaman çizelgesi.
//
// İkisi de veriyi kiracının PORTAL kayıtlarından çeker (PwsPortfolioItem /
// PwsResumeEntry). Bölüm ayarına elle iş/deneyim girilmez: aynı bilgiyi iki
// yerde tutmak, birini güncelleyip diğerini unutmak demektir.
//
// Yayın denetimi SUNUCUDA: taslak iş (Status != published) ve gizlenmiş
// özgeçmiş satırı (IsVisible = false) uca hiç gelmez.
//
// Kayıt yoksa bölüm HİÇ BASILMAZ — örnek proje/sahte deneyim gösterilmez.

import { Component, OnInit, computed, inject, input, signal } from '@angular/core';
import { SiteConfig, cfg } from './section.model';
import { SiteChromeText } from '../core/site-chrome-text';
import { TenantWebService, TenantPortfolioItem, TenantResumeEntry } from '../core/tenant-web.service';

/** Yıl damgası — ziyaretçinin saat dilimi tarihi KAYDIRMAMALI. */
function yil(iso: string | null | undefined): string {
  if (!iso) return '';
  const m = /^(\d{4})/.exec(iso);
  return m ? m[1] : '';
}

// ══════════════════════════════════════════════════════════════════════════
// PORTFOLYO IZGARASI
// ══════════════════════════════════════════════════════════════════════════

@Component({
  selector: 'sec-portfolio-grid',
  standalone: true,
  template: `
    @if (isler().length) {
    <section id="portfolio" class="bg-white py-24 dark:bg-neutral-950">
      <div class="mx-auto max-w-7xl px-6 lg:px-8">
        <div class="text-center">
          @if (eyebrow()) {
            <p class="text-sm font-semibold uppercase tracking-widest" style="color:var(--site-accent)">{{ eyebrow() }}</p>
          }
          <h2 class="mt-3 font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">{{ title() }}</h2>
          @if (subtitle()) {
            <p class="mx-auto mt-4 max-w-2xl text-lg text-neutral-600 dark:text-neutral-400">{{ subtitle() }}</p>
          }
        </div>

        <div class="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          @for (i of isler(); track i.id) {
            <!-- Bağlantısı olmayan iş TIKLANMAZ olmalı: boş bir <a> ziyaretçiyi
                 hiçbir yere götürmeyen bir tıklamaya davet ederdi. -->
            <a [href]="i.projectUrl || null" [attr.target]="i.projectUrl ? '_blank' : null"
               [attr.rel]="i.projectUrl ? 'noopener noreferrer' : null"
               class="group flex flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white transition dark:border-neutral-800 dark:bg-neutral-900"
               [class.hover:shadow-lg]="!!i.projectUrl"
               [class.cursor-default]="!i.projectUrl">
              <div class="aspect-video overflow-hidden bg-neutral-100 dark:bg-neutral-800">
                @if (i.coverImageUrl) {
                  <img [src]="i.coverImageUrl" [alt]="i.title" class="h-full w-full object-cover transition group-hover:scale-105" />
                }
              </div>
              <div class="flex flex-1 flex-col p-5">
                <div class="flex items-center gap-2">
                  @if (yilOf(i)) { <span class="text-xs font-medium uppercase tracking-wide text-neutral-400">{{ yilOf(i) }}</span> }
                  @if (i.isFeatured) {
                    <span class="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white" style="background:var(--site-accent)">{{ text.t('pf.featured') }}</span>
                  }
                </div>
                <h3 class="mt-1 text-lg font-bold text-neutral-900 dark:text-white">{{ i.title }}</h3>
                @if (i.clientName) {
                  <p class="mt-0.5 text-sm text-neutral-500 dark:text-neutral-400">{{ text.t('pf.client') }}: {{ i.clientName }}</p>
                }
                @if (i.summary) {
                  <p class="mt-2 line-clamp-3 text-sm leading-relaxed text-neutral-600 dark:text-neutral-400">{{ i.summary }}</p>
                }
                @if (etiketler(i).length) {
                  <div class="mt-3 flex flex-wrap gap-1.5">
                    @for (e of etiketler(i); track e) {
                      <span class="rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">{{ e }}</span>
                    }
                  </div>
                }
                @if (i.projectUrl) {
                  <span class="mt-4 inline-flex items-center gap-1 text-sm font-semibold" style="color:var(--site-primary)">{{ text.t('pf.view') }}</span>
                }
              </div>
            </a>
          }
        </div>
      </div>
    </section>
    }
  `,
})
export class PortfolioGridSection implements OnInit {
  protected text = inject(SiteChromeText);
  private svc = inject(TenantWebService);

  config = input<SiteConfig>({});
  clientId = input<number>(0);

  protected isler = signal<TenantPortfolioItem[]>([]);

  ngOnInit() {
    const id = this.clientId();
    if (!id) return;

    const qty = Number(cfg(this.config(), 'count', 6)) || 6;
    // Select değeri METİN döner; ham boolean karşılaştırması 'false' dizesini
    // doğru sayardı.
    const sadeceOne = String(cfg(this.config(), 'featuredOnly', 'false')) === 'true';

    this.svc.getPortfolio(id, qty, sadeceOne).subscribe({
      next: (l) => this.isler.set(l),
      error: () => this.isler.set([]),
    });
  }

  protected eyebrow = computed(() => cfg(this.config(), 'eyebrow', ''));
  protected title = computed(() => cfg(this.config(), 'title', this.text.t('fb.calismalarim')));
  protected subtitle = computed(() => cfg(this.config(), 'subtitle', ''));

  protected yilOf = (i: TenantPortfolioItem) => yil(i.completedAt);

  protected etiketler = (i: TenantPortfolioItem): string[] =>
    (i.tags || '').split(',').map(t => t.trim()).filter(Boolean).slice(0, 4);
}

// ══════════════════════════════════════════════════════════════════════════
// ÖZGEÇMİŞ ZAMAN ÇİZELGESİ
// ══════════════════════════════════════════════════════════════════════════

@Component({
  selector: 'sec-resume-timeline',
  standalone: true,
  template: `
    @if (deneyim().length || egitim().length || beceri().length) {
    <section id="resume" class="bg-neutral-50 py-24 dark:bg-neutral-900">
      <div class="mx-auto max-w-4xl px-6 lg:px-8">
        <div class="text-center">
          @if (eyebrow()) {
            <p class="text-sm font-semibold uppercase tracking-widest" style="color:var(--site-accent)">{{ eyebrow() }}</p>
          }
          <h2 class="mt-3 font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">{{ title() }}</h2>
        </div>

        @if (deneyim().length) {
          <h3 class="mt-12 text-sm font-bold uppercase tracking-widest text-neutral-500 dark:text-neutral-400">{{ text.t('cv.experience') }}</h3>
          <ol class="mt-5 space-y-6 border-l-2 border-neutral-200 pl-6 dark:border-neutral-700">
            @for (r of deneyim(); track r.id) {
              <li class="relative">
                <span class="absolute -left-[1.9rem] top-1.5 h-3 w-3 rounded-full ring-4 ring-neutral-50 dark:ring-neutral-900" style="background:var(--site-primary)"></span>
                <p class="text-xs font-semibold uppercase tracking-wide text-neutral-400">{{ range(r) }}</p>
                <p class="mt-0.5 text-base font-bold text-neutral-900 dark:text-white">{{ r.title }}</p>
                @if (r.organization) {
                  <p class="text-sm text-neutral-600 dark:text-neutral-400">
                    {{ r.organization }}@if (r.location) {<span> · {{ r.location }}</span>}
                  </p>
                }
                @if (r.description) {
                  <p class="mt-1.5 text-sm leading-relaxed text-neutral-600 dark:text-neutral-400">{{ r.description }}</p>
                }
              </li>
            }
          </ol>
        }

        @if (egitim().length) {
          <h3 class="mt-12 text-sm font-bold uppercase tracking-widest text-neutral-500 dark:text-neutral-400">{{ text.t('cv.education') }}</h3>
          <ol class="mt-5 space-y-6 border-l-2 border-neutral-200 pl-6 dark:border-neutral-700">
            @for (r of egitim(); track r.id) {
              <li class="relative">
                <span class="absolute -left-[1.9rem] top-1.5 h-3 w-3 rounded-full ring-4 ring-neutral-50 dark:ring-neutral-900" style="background:var(--site-accent)"></span>
                <p class="text-xs font-semibold uppercase tracking-wide text-neutral-400">{{ range(r) }}</p>
                <p class="mt-0.5 text-base font-bold text-neutral-900 dark:text-white">{{ r.title }}</p>
                @if (r.organization) { <p class="text-sm text-neutral-600 dark:text-neutral-400">{{ r.organization }}</p> }
              </li>
            }
          </ol>
        }

        @if (beceri().length) {
          <h3 class="mt-12 text-sm font-bold uppercase tracking-widest text-neutral-500 dark:text-neutral-400">{{ text.t('cv.skills') }}</h3>
          <div class="mt-5 space-y-4">
            @for (r of beceri(); track r.id) {
              <div>
                <div class="flex items-baseline justify-between">
                  <span class="text-sm font-semibold text-neutral-900 dark:text-white">{{ r.title }}</span>
                  @if (r.skillLevel != null) {
                    <span class="text-xs tabular-nums text-neutral-400">%{{ r.skillLevel }}</span>
                  }
                </div>
                <!-- Düzey girilmemişse çubuk HİÇ çizilmiyor: %0 dolu bir çubuk
                     "bu beceri yok" gibi okunurdu. -->
                @if (r.skillLevel != null) {
                  <div class="mt-1.5 h-2 overflow-hidden rounded-full bg-neutral-200 dark:bg-neutral-800">
                    <div class="h-full rounded-full" [style.width.%]="r.skillLevel" style="background:var(--site-primary)"></div>
                  </div>
                }
              </div>
            }
          </div>
        }
      </div>
    </section>
    }
  `,
})
export class ResumeTimelineSection implements OnInit {
  protected text = inject(SiteChromeText);
  private svc = inject(TenantWebService);

  config = input<SiteConfig>({});
  clientId = input<number>(0);

  private rows = signal<TenantResumeEntry[]>([]);

  ngOnInit() {
    const id = this.clientId();
    if (!id) return;

    // Tür filtresi UÇTA değil burada: üç grubu tek istekle alıp ayırmak
    // üç ayrı çağrıdan ucuz.
    this.svc.getResume(id).subscribe({
      next: (l) => this.rows.set(l),
      error: () => this.rows.set([]),
    });
  }

  protected eyebrow = computed(() => cfg(this.config(), 'eyebrow', ''));
  protected title = computed(() => cfg(this.config(), 'title', this.text.t('fb.ozgecmis')));

  private tur = (t: string) => this.rows().filter(r => (r.entryType || '').toLowerCase() === t);

  protected deneyim = computed(() => this.tur('experience'));
  protected egitim = computed(() => this.tur('education'));
  protected beceri = computed(() => this.tur('skill'));

  /** "2021 — Devam ediyor" / "2018 — 2021" / "2019" */
  protected range = (r: TenantResumeEntry): string => {
    const bas = yil(r.startDate);
    if (r.isCurrent) return bas ? `${bas} — ${this.text.t('cv.present')}` : this.text.t('cv.present');
    const last = yil(r.endDate);
    if (bas && last) return bas === last ? bas : `${bas} — ${last}`;
    return bas || last;
  };
}
